import {
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  signal,
  viewChild,
} from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import { forkJoin, of, switchMap, catchError, map } from "rxjs";
import { ProjectService } from "../../../shared/services/project.service";
import { ShotOverlayDto } from "../../../models/shotOverlayDto";
import { ImageLoaderService } from "../../../shared/services/image-loader.service";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { SliceType } from "../../../models/sliceType";
import { ModelType } from "../../../models/modelType";
import { CachedPaletteService } from "../../../projects/services/cached-palette.service";
import { AgentViz, ShotLocations } from "../xwai-viz.types";
import { allSliceProcessings } from "../../../projects/project/model-new/slice_processings";
import { ImageFlipperService, ShotOverlayPoint } from "../../../copilot/image-flipper.service";
import { mergeShotOverlay, shotFitColorScale } from "../shot-overlay.utils";
import { SharedModule } from "../../../shared/shared.module";
import { NzPopoverModule } from "ng-zorro-antd/popover";
import { XwaiSliceViewerComponent } from "../xwai-slice-viewer/xwai-slice-viewer.component";
import { XwaiV2StreamService } from "../../stream/xwai-v2-stream.service";
import { vizParamsPatch } from "../viz-params";

const VP_REF_MODELS = ['Total_Vp_Update']
/** Marks a model type whose values are symmetric about zero (min = -max). */
const ZERO_CENTRED = "_zero_centred_params_process";

/** Model axes a crop bound can be stored against in viz_params. */
type CropAxis = "inline" | "crossline" | "depth";

/** Resolved slice payload, ready to hand to the {@link XwaiSliceViewerComponent}. */
interface RenderedSlice {
  sliceImage: string;
  sliceArray: number[][];
  colorbar: string;
  limits: { min: number; max: number };
  xLabel: string;
  yLabel: string;
  flippedYAxis: boolean;
  /** Depth-slice-only opt-out from the corrected axis domains; see `sliceLayout`. */
  transposeDomains: boolean;
}

/**
 * Renders a "model" visualization: a velocity/property model slice with model
 * type, colormap, and iteration controls. Driven entirely by the agent's
 * `viz_params`, so no extra job lookup is needed beyond fetching the slice.
 */
@Component({
  selector: "app-xwai-model-viz",
  templateUrl: "./xwai-model-viz.component.html",
  styleUrls: ["./xwai-model-viz.component.less"],
  imports: [SharedModule, NzPopoverModule, XwaiSliceViewerComponent],
})
export class XwaiModelVizComponent {
  private readonly projectService = inject(ProjectService);
  private readonly stream = inject(XwaiV2StreamService);
  private readonly cachedProjectService = inject(CachedProjectService);
  private readonly imageLoader = inject(ImageLoaderService);
  private readonly imageFlipper = inject(ImageFlipperService);
  private readonly cachedPaletteService = inject(CachedPaletteService);

  /** Colormap names for the picker, fetched (and cached) from the palette service. */
  protected readonly availableColormaps = rxResource({
    stream: () => this.cachedPaletteService.getPalettes(),
  });

  readonly viz = input.required<AgentViz>();

  readonly job = computed(() => this.viz().job);

  protected image_plot_url: string | undefined;
  protected image_object_url: string | undefined;

  private readonly SLICE_DIM_LABELS: Record<number, string> = {
    [SliceType.inline]: "inline",
    [SliceType.crossLine]: "crossline",
    [SliceType.depth]: "depth",
  };

  protected modelType = linkedSignal(
    () => this.viz().viz_params.model_type ?? "—",
  );
  protected cmap = linkedSignal(() =>
    this.modelType() === "RWI_Perturb"
      ? "greyscale"
      : (this.viz().viz_params.cmap ?? "—"),
  );
  protected iteration = linkedSignal(() => this.viz().viz_params.iteration);
  protected dx = computed(() => this.viz().job.model_grid.dx ?? 1);

  /** Commit the iteration only when the slider handle is released (not mid-drag). */
  protected onIterationRelease(value: number | number[]) {
    this.iteration.set(Array.isArray(value) ? value[0] : value);
  }

  protected vMin = linkedSignal(() => this.viz().viz_params.min ?? null)
  protected vMax = linkedSignal(() => this.viz().viz_params.max ?? null)
  protected vpRef = linkedSignal<number | null>(
    () => this.viz().viz_params.vp_ref ?? null,
  );
  protected isDepthSlice = computed(() => this.viz().viz_params.slice_dim === 3)
  protected isVpRefModel = computed(() => this.modelType() in VP_REF_MODELS)
  // Seeded from viz_params.show_shots (depth slices may request shots up front),
  // then user-toggleable via the switch.
  protected showShotOverlay = linkedSignal(() => this.viz().viz_params.show_shots ?? false)
  // Editable colour-mapping range for the shot overlay (by traces_fit, 0–100).
  protected shotColorMin = signal<number>(50)
  protected shotColorMax = signal<number>(90)

  /**
   * Whether the selected model type is symmetric about zero. Such models expose
   * a single limit: the user sets the magnitude and min = -max. Falls back to a
   * separate min/max when the type isn't zero-centred (or is unknown).
   */
  protected isZeroCentred = computed(() =>
    (
      (allSliceProcessings as Record<string, string[]>)[this.modelType()] ?? []
    ).includes(ZERO_CENTRED),
  );

  /**
   * Draft values bound to the deferred inputs. Editing these does NOT re-fetch —
   * only {@link applyChanges} commits them into {@link vMin} / {@link vMax} /
   * {@link vpRef}, which the slice resource depends on. Seeded from (and reset
   * by) the applied values. `?? null` preserves 0 as an explicit value.
   */
  protected draftMin = linkedSignal(() => this.vMin());
  protected draftMax = linkedSignal(() => this.vMax());
  /** Single symmetric limit for zero-centred models; seeded from the max. */
  protected draftRange = linkedSignal(() => this.vMax());
  protected draftVpRef = linkedSignal(() => this.vpRef());

  // ── Crop ────────────────────────────────────────────────────────────────────
  // The applied crop window, in metres, seeded from the agent payload or from a
  // reloaded conversation. null on a bound means "full extent" for that edge.
  // Cropping is client-side only (see XwaiSliceViewerComponent), so committing it
  // never re-fetches the slice — none of these signals are read by sliceResource.
  // The params are named per model axis (inline_min, depth_max, …); cropKeys maps
  // the viewer's x/y onto them for the current slice.
  protected cropXMin = linkedSignal(() => this.cropParam(this.cropKeys().x, "min"));
  protected cropXMax = linkedSignal(() => this.cropParam(this.cropKeys().x, "max"));
  protected cropYMin = linkedSignal(() => this.cropParam(this.cropKeys().y, "min"));
  protected cropYMax = linkedSignal(() => this.cropParam(this.cropKeys().y, "max"));

  private cropParam(axis: CropAxis, bound: "min" | "max"): number | null {
    return this.viz().viz_params[`${axis}_${bound}`] ?? null;
  }

  /** Drafts bound to the crop popover inputs; committed by {@link applyChanges}. */
  protected draftCropXMin = linkedSignal(() => this.cropXMin());
  protected draftCropXMax = linkedSignal(() => this.cropXMax());
  protected draftCropYMin = linkedSignal(() => this.cropYMin());
  protected draftCropYMax = linkedSignal(() => this.cropYMax());

  /** True while the user is dragging a crop box on the slice. */
  protected cropMode = signal(false);

  /**
   * Slice geometry, derived from the model grid alone so it stays stable while a
   * refetch is in flight (a crop must not blink off every time the iteration or
   * colormap changes).
   */
  private layout = computed(() => {
    const job = this.viz().job;
    return this.sliceLayout(
      this.viz().viz_params.slice_dim,
      job.model_grid,
      job.x2_is_inline ?? job.model_grid["x2_is_inline"] ?? false,
    );
  });

  /**
   * Full slice extent in metres, matching the axes the viewer actually renders:
   * `width` (the row length) is the x domain and `height` (the row count) the y
   * domain — except on a depth slice, where `transposeDomains` keeps the old
   * swapped behaviour. Null means "crop unavailable", the same guard as the v1
   * copilot's `crop_available`.
   */
  protected extentMeters = computed(() => {
    const { width, height, transposeDomains } = this.layout();
    const dx = this.dx();
    if (!width || !height || !dx) return null;
    return transposeDomains
      ? { x: height * dx, y: width * dx }
      : { x: width * dx, y: height * dx };
  });

  /** Axis names for the crop labels ("Crossline"/"Depth", …), from the slice layout. */
  protected axisLabels = computed(() => ({
    x: this.layout().xLabel,
    y: this.layout().yLabel,
  }));

  /** viz_params crop prefix for each viewer axis, e.g. x → "crossline". */
  private cropKeys = computed(() => ({
    x: this.layout().xLabel.toLowerCase() as CropAxis,
    y: this.layout().yLabel.toLowerCase() as CropAxis,
  }));

  /** True when the applied crop actually narrows the slice. */
  protected hasCrop = computed(() => {
    const extent = this.extentMeters();
    if (!extent) return false;
    return (
      (this.cropXMin() ?? 0) > 0 ||
      (this.cropYMin() ?? 0) > 0 ||
      (this.cropXMax() ?? extent.x) < extent.x ||
      (this.cropYMax() ?? extent.y) < extent.y
    );
  });

  /**
   * Crop window handed to the viewer, or null to keep its un-cropped fast path.
   */
  protected xCropM = computed<number[] | null>(() => {
    const extent = this.extentMeters();
    if (!extent || !this.hasCrop()) return null;
    return [this.cropXMin() ?? 0, this.cropXMax() ?? extent.x];
  });
  protected yCropM = computed<number[] | null>(() => {
    const extent = this.extentMeters();
    if (!extent || !this.hasCrop()) return null;
    return [this.cropYMin() ?? 0, this.cropYMax() ?? extent.y];
  });

  /** True when a draft differs from what's currently applied/rendered. */
  protected pendingChanges = computed(() => {
    const limitsChanged = this.isZeroCentred()
      ? this.draftRange() !== this.vMax()
      : this.draftMin() !== this.vMin() || this.draftMax() !== this.vMax();
    const cropChanged =
      this.draftCropXMin() !== this.cropXMin() ||
      this.draftCropXMax() !== this.cropXMax() ||
      this.draftCropYMin() !== this.cropYMin() ||
      this.draftCropYMax() !== this.cropYMax();
    return limitsChanged || cropChanged || this.draftVpRef() !== this.vpRef();
  });

  /** Commit the draft controls, triggering a single re-fetch. */
  protected applyChanges(): void {
    if (this.isZeroCentred()) {
      const range = this.draftRange();
      this.vMax.set(range);
      this.vMin.set(range == null ? null : -range);
    } else {
      this.vMin.set(this.draftMin());
      this.vMax.set(this.draftMax());
    }
    this.vpRef.set(this.draftVpRef());
    this.applyCrop();
  }

  /**
   * Commits the crop drafts, clamped to the model extent. A window with no
   * positive area is dropped rather than reported, mirroring the v1 copilot's
   * `resolve_crop_bounds`; the inputs' nzMin/nzMax already keep sane values in.
   */
  private applyCrop(): void {
    const extent = this.extentMeters();
    if (!extent) return;

    const clamp = (value: number | null, max: number) =>
      value == null ? null : Math.min(Math.max(value, 0), max);

    const xMin = clamp(this.draftCropXMin(), extent.x);
    const xMax = clamp(this.draftCropXMax(), extent.x);
    const yMin = clamp(this.draftCropYMin(), extent.y);
    const yMax = clamp(this.draftCropYMax(), extent.y);

    const emptyX = xMin != null && xMax != null && xMax <= xMin;
    const emptyY = yMin != null && yMax != null && yMax <= yMin;
    if (emptyX || emptyY) {
      // Put the drafts back to what's applied so the inputs don't sit on a
      // region that was silently ignored.
      this.draftCropXMin.set(this.cropXMin());
      this.draftCropXMax.set(this.cropXMax());
      this.draftCropYMin.set(this.cropYMin());
      this.draftCropYMax.set(this.cropYMax());
      return;
    }

    this.cropXMin.set(xMin);
    this.cropXMax.set(xMax);
    this.cropYMin.set(yMin);
    this.cropYMax.set(yMax);
    this.draftCropXMin.set(xMin);
    this.draftCropXMax.set(xMax);
    this.draftCropYMin.set(yMin);
    this.draftCropYMax.set(yMax);
  }

  /** Clears the crop drafts back to the full extent (applied on Apply). */
  protected resetCropDraft(): void {
    this.draftCropXMin.set(null);
    this.draftCropXMax.set(null);
    this.draftCropYMin.set(null);
    this.draftCropYMax.set(null);
  }

  /**
   * A crop box was dragged on the slice: fill the drafts from it and apply right
   * away (the numbers stay editable for fine tuning), then leave crop mode.
   */
  protected onCropSelected(region: {
    x: [number, number];
    y: [number, number];
  }): void {
    this.draftCropXMin.set(Math.round(region.x[0]));
    this.draftCropXMax.set(Math.round(region.x[1]));
    this.draftCropYMin.set(Math.round(region.y[0]));
    this.draftCropYMax.set(Math.round(region.y[1]));
    this.cropMode.set(false);
    this.applyCrop();
  }

  constructor() {
    // Mirror every *committed* control back into the conversation's viz_params, so
    // saving/updating the conversation persists what the user tuned rather than
    // what the agent originally emitted. Drafts are deliberately not read here —
    // nothing is written until Apply. On first render these all equal viz_params,
    // and the service's diff makes that a no-op.
    effect(() => {
      const current = this.viz().viz_params as unknown as Record<string, unknown>;
      // Third element is the seed the control falls back to when the agent omitted
      // the param — vizParamsPatch drops those so an untouched viz isn't "edited".
      this.stream.persistVizParams(
        this.viz(),
        vizParamsPatch(current, [
          ["model_type", this.modelType(), "—"],
          ["cmap", this.cmap(), "—"],
          ["iteration", this.iteration()],
          ["min", this.vMin(), null],
          ["max", this.vMax(), null],
          ["vp_ref", this.vpRef(), null],
          ["show_shots", this.showShotOverlay(), false],
          [`${this.cropKeys().x}_min`, this.cropXMin(), null],
          [`${this.cropKeys().x}_max`, this.cropXMax(), null],
          [`${this.cropKeys().y}_min`, this.cropYMin(), null],
          [`${this.cropKeys().y}_max`, this.cropYMax(), null],
        ]),
      );
    });
  }

  /** The live viewer, so snapshots can read the cropped image that's on screen. */
  private readonly viewer = viewChild(XwaiSliceViewerComponent);

  /**
   * The min/max actually applied to the rendered slice. When {@link vMin} /
   * {@link vMax} are left empty (null) the backend falls back to the model's
   * global limits, which come back on the result — we surface those as the
   * input placeholders so the user can see the effective range.
   */
  protected effectiveLimits = computed(
    () => this.sliceResource.value()?.limits ?? null,
  );

  private modelTypeParams = computed(() => {
    return {
      id: this.job().id,
      iterations: this.viz().viz_params.iteration,
    };
  });

  protected readonly modelTypesAvailable = rxResource<
    ModelType[],
    { id: string; iterations: number }
  >({
    params: () => this.modelTypeParams(),
    stream: ({ params }) => {
      return (
        this.cachedProjectService.getModelTypes(params.id, params.iterations) ??
        of([])
      );
    },
  });

  /**
   * Fetch the model image. The agent supplies everything `getSlice` needs —
   * model type, colormap, iteration, the slice to cut, and the grid's 2D/3D
   * flag — so we call the plot endpoint directly with no job lookup. We then
   * resolve the rendered PNG to base64 and decode the binary slice array,
   * producing a {@link RenderedSlice} for the viewer.
   */
  protected readonly sliceResource = rxResource<RenderedSlice, any>({
    params: () => {
      const v = this.viz();
      if (!v || !v.job) return undefined;
      const p = v.viz_params;
      return {
        jobId: v.job.id,
        modelType: this.modelType(),
        cmap: this.cmap(),
        iteration: this.iteration(),
        sliceDim: p.slice_dim,
        sliceNumber: p.slice_number,
        grid: v.job.model_grid,
        is2d: v.job.model_grid.is_2d ?? false,
        x2IsInline: v.job.x2_is_inline ?? v.job.model_grid["x2_is_inline"] ?? false,
        v_min: this.vMin(),
        v_max: this.vMax(),
        vp_ref: this.vpRef(),
      };
    },
    stream: ({ params }) =>
      this.projectService
        .getSlice(
          params.jobId,
          params.modelType,
          params.iteration,
          params.sliceDim,
          params.sliceNumber,
          params.is2d,
          params.cmap,
          params.v_min,
          params.v_max,
          undefined, // shotNumber
          undefined, // fwd
          params.vp_ref,
        )
        .pipe(
          switchMap((res) => {
            const { url, colorBarUrl, sliceUrl, min, max, trueMin, trueMax } =
              res;
            const { width, height, xLabel, yLabel, flippedYAxis, transposeDomains } =
              this.sliceLayout(params.sliceDim, params.grid, params.x2IsInline);

            return forkJoin({
              sliceImage: this.imageLoader.getImageBase64(url),
              colorbar: of(colorBarUrl),
              sliceArray: this.projectService.getSliceArray(
                sliceUrl,
                width,
                height,
                trueMin,
                trueMax,
              ),
              limits: of({ min, max }),
              xLabel: of(xLabel),
              yLabel: of(yLabel),
              flippedYAxis: of(flippedYAxis),
              transposeDomains: of(transposeDomains),
            });
          }),
          catchError((err) => {
            throw Error(
              err?.error?.detail ||
                err?.message ||
                "An unexpected error occurred",
            );
          }),
        ),
  });

  /**
   * Maps a slice dimension + model grid to the array dimensions and axis labels
   * the viewer needs.
   *
   * `slice_dim` is the grid axis being cut, and which axis is inline depends on
   * `x2_is_inline`:
   *   - default (`x2_is_inline` false): axis 1 = inline (nx1), axis 2 = crossline (nx2)
   *   - `x2_is_inline` true:            axis 1 = crossline,    axis 2 = inline
   * So an inline section is `slice_dim` 1 by default and 2 when the flag is set.
   * An inline or crossline section always has depth on y; x is the other in-plane
   * axis (inline section → x = crossline, crossline section → x = inline). A
   * depth section is a map view of the two lateral axes.
   *
   * `width` is the row length (the viewer's x domain) and `height` the row count
   * (its y domain) — the argument order `getSliceArray` expects.
   *
   * These expressions mirror the model page's inline/crossline slice components,
   * but *resolved to raw grid axes*: that page reads a DTO whose nx1/nx2 have
   * already been swapped by `getJobDetail` (project.service.ts:302-303, under a
   * TODO about exactly this confusion), whereas the agent hands us the raw
   * `model_grid`. So its expressions cannot be copied verbatim — folding the DTO
   * swap in is what produces the widths below.
   */
  private sliceLayout(
    sliceDim: number,
    grid: AgentViz["job"]["model_grid"],
    x2IsInline: boolean,
  ): {
    width: number;
    height: number;
    xLabel: string;
    yLabel: string;
    flippedYAxis: boolean;
    transposeDomains: boolean;
  } {
    // Named after the *section*, not the axis it is labelled with — under the
    // model page's labelling those are not the same thing (see the depth note).
    const inlineSectionWidth = x2IsInline ? grid.nx2 : grid.nx1;
    const crosslineSectionWidth = x2IsInline ? grid.nx1 : grid.nx2;
    const inlineDim = x2IsInline ? SliceType.inline : SliceType.crossLine;
    const crosslineDim = x2IsInline ? SliceType.crossLine : SliceType.inline;

    switch (sliceDim) {
      case inlineDim: // inline section: crossline × depth plane
        return {
          width: inlineSectionWidth,
          height: grid.nx3,
          xLabel: "Crossline",
          yLabel: "Depth",
          flippedYAxis: true,
          transposeDomains: false,
        };
      case SliceType.depth: // depth section: crossline × inline map view
        return {
          width: crosslineSectionWidth,
          height: inlineSectionWidth,
          xLabel: "Crossline",
          yLabel: "Inline",
          flippedYAxis: false,
          // The depth slice's axis assignment is still undecided: its reshape
          // (row length = crossline) and its rendering (row *count* on x) have
          // always disagreed, and it also disagrees with the model page's depth
          // slice. Transposing the viewer's domains here reproduces the existing
          // behaviour exactly, so fixing the sections above changes nothing for
          // depth. Delete this flag (and the viewer input) once depth is settled.
          transposeDomains: true,
        };
      case crosslineDim: // crossline section: inline × depth plane
      default:
        return {
          width: crosslineSectionWidth,
          height: grid.nx3,
          xLabel: "Inline",
          yLabel: "Depth",
          flippedYAxis: true,
          transposeDomains: false,
        };
    }
  }

  /** Maps a {@link SliceType} value to the label used by the image flipper. */

  protected add_image_to_flipper(): void {
    const sliceImage = this.sliceResource.value()?.sliceImage;
    if (!sliceImage) return;
    // Pin what's on screen — the cropped canvas re-render when a crop is active.
    const image = this.viewer()?.renderedImage() || sliceImage;

    this.imageFlipper.add_image(
      {
        image_link: image,
        image_information: {
          job_name: this.job().job_name,
          iteration: this.iteration(),
          slice_number: String(this.viz().viz_params.slice_number),
          dimension:
            this.SLICE_DIM_LABELS[
              this.viz().viz_params.slice_dim
            ],
        },
      },
      this.shotOverlayPoints(),
    );
  }

  /**
   * Snapshots the shot overlay (if currently shown on a depth slice) as points
   * normalized to the slice image (0–1, left→right, top→bottom) — mirrors the
   * positioning math in {@link XwaiSliceViewerComponent.positionShots}, since the
   * flipper only ever gets the flat PNG, not the live D3 overlay.
   *
   * Normalization is against the *visible* window, so shots stay on their
   * features when a crop is applied; shots outside it are dropped.
   */
  private shotOverlayPoints(): ShotOverlayPoint[] | undefined {
    if (!this.isDepthSlice() || !this.showShotOverlay()) return undefined;

    const fits = this.shotsOverlayResource.value();
    const locations = this.shotLocsResource.value();
    const sliceArray = this.sliceResource.value()?.sliceArray;
    if (!fits?.length || !sliceArray?.length) return undefined;

    const dx = this.dx();
    const gridWidth = sliceArray.length;
    const gridHeight = sliceArray[0].length;
    // Window in sample indices: the crop when active, else the whole grid.
    const [x1, x2] = this.xCropM()?.map((m) => m / dx) ?? [0, gridWidth];
    const [y1, y2] = this.yCropM()?.map((m) => m / dx) ?? [0, gridHeight];
    const color = shotFitColorScale(this.shotColorMin(), this.shotColorMax());

    return mergeShotOverlay(fits, locations)
      .map((shot) => ({
        x: (shot.sx / dx - x1) / (x2 - x1),
        y: 1 - (shot.sy / dx - y1) / (y2 - y1),
        color: color(shot.traces_fit),
        shot_id: shot.shot_id,
        traces_fit: shot.traces_fit,
        sx: shot.sx,
        sy: shot.sy,
        sz: shot.sz,
      }))
      .filter((p) => p.x >= 0 && p.x <= 1 && p.y >= 0 && p.y <= 1);
  }

  private shotOverlayParams = computed(() => {
    if (this.isDepthSlice()) {
      if (this.showShotOverlay()) return {id: this.job().id, iteration: this.iteration()}
      else return null
    }
  })
  protected readonly shotsOverlayResource = rxResource({
    params: this.shotOverlayParams,
    // defaultValue keeps the resource value ShotOverlayDto[] (never undefined),
    // even during the transient loading state.
    defaultValue: [] as ShotOverlayDto[],
    stream: ({params}) => {
      if (!params) return of<ShotOverlayDto[]>([])
        // we pass params null specifically when it should not be shown, for which case we will set the shots array as []
      return this.cachedProjectService.getShotOverlay(this.job().id, params.iteration)
    }
  })
  
  private shotLocParams = computed(() => {
    if (this.isDepthSlice()) {
      if (this.showShotOverlay()) return {id: this.job().id, iterations_complete: this.job().iterations_completed}
      else return null
    }
  })
  // Shot source locations keyed by shot number, merged with the overlay fits to
  // place shots on a depth slice. defaultValue keeps the value ShotLocations
  // (never undefined), even during the transient loading state.
  protected readonly shotLocsResource = rxResource({
    params: this.shotLocParams,
    defaultValue: {} as ShotLocations,
    stream: ({params}) => {
      // params is null when the overlay should not be shown → empty dict.
      if (!params) return of<ShotLocations>({})

      return this.cachedProjectService
        .getCosts(params.id, params.iterations_complete)
        .pipe(
          map((data) =>
            data?.costItems?.reduce<ShotLocations>((acc, shot) => {
              acc[shot.shotNo] = { sx: shot.sx, sy: shot.sy, sz: shot.sz };
              return acc;
            }, {}) ?? {},
          ),
        )
    }
  })
}
